import React, { useEffect, useState } from 'react';
import { fetchWithTimeout } from '../../lib/fetchWithTimeout';
import {
  Gavel,
  Loader,
  AlertCircle,
  CalendarClock,
  CheckSquare,
  ArrowRightLeft,
  Users,
  ScrollText,
} from 'lucide-react';

const backendUrl =
  (import.meta as any).env?.VITE_BACKEND_API_URL ||
  'https://fcc-summarize-app-r4ac3.ondigitalocean.app';

type Priority = 'immediate' | 'near_term' | 'long_term' | string;

interface ComplianceObligation {
  action_required: string;
  affected_entities?: string[];
  deadline?: string | null;
  deadline_text?: string | null;
  paragraph_citation?: string | null;
  obligation_type?: string | null;
  priority?: Priority;
}

interface RuleChange {
  rule_section?: string | null;
  prior_rule?: string | null;
  new_rule: string;
  effective_date?: string | null;
  paragraph_citation?: string | null;
  change_type?: string;
  impact_level?: string;
}

interface CommissionerVote {
  commissioner_name: string;
  vote: string;
  has_statement?: boolean;
  statement_summary?: string | null;
}

interface ROAnalysis {
  executive_summary?: string;
  key_takeaways?: string[];
  rule_changes?: RuleChange[];
  compliance_obligations?: ComplianceObligation[];
  affected_entities?: string[];
  votes?: CommissionerVote[];
  vote_breakdown?: string;
  reconsideration_deadline?: string | null;
  effective_date?: string | null;
  adoption_date?: string | null;
  release_date?: string | null;
  implementation_timeline?: string | null;
  financial_impact?: string | null;
}

interface OrderAnalysisResponse {
  status: 'ready' | 'no_order' | 'error';
  submission_id?: string;
  order?: { filer_name?: string; date_received?: string };
  analysis?: ROAnalysis;
  cached?: boolean;
  detail?: string;
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const priorityStyles: Record<string, string> = {
  immediate: 'bg-red-100 text-red-700 border-red-200',
  near_term: 'bg-amber-100 text-amber-700 border-amber-200',
  long_term: 'bg-slate-100 text-slate-600 border-slate-200',
};

const priorityLabel: Record<string, string> = {
  immediate: 'Immediate',
  near_term: 'Near-term',
  long_term: 'Longer-term',
};

function PriorityBadge({ priority }: { priority?: Priority }) {
  const key = (priority || 'near_term').toLowerCase();
  const cls = priorityStyles[key] || priorityStyles.near_term;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {priorityLabel[key] || priority}
    </span>
  );
}

export function OrderSection({ docketNumber }: { docketNumber: string }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'no_order' }
    | { kind: 'error'; detail?: string }
    | { kind: 'ready'; data: OrderAnalysisResponse }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchWithTimeout(
      `${backendUrl}/api/proceedings/${encodeURIComponent(
        docketNumber
      )}/order-analysis`
    )
      .then((r) => r.json())
      .then((data: OrderAnalysisResponse) => {
        if (cancelled) return;
        if (data.status === 'no_order') setState({ kind: 'no_order' });
        else if (data.status === 'ready') setState({ kind: 'ready', data });
        else setState({ kind: 'error', detail: data.detail });
      })
      .catch((e) => {
        if (!cancelled) setState({ kind: 'error', detail: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [docketNumber]);

  if (state.kind === 'loading') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-6 text-slate-600">
        <Loader className="h-5 w-5 animate-spin text-slate-500" />
        <span>Reading the order and pulling out what it requires…</span>
      </div>
    );
  }

  if (state.kind === 'no_order') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
        <div className="flex items-start gap-3">
          <Gavel className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-400" />
          <div>
            <h3 className="font-semibold text-slate-800">No order yet</h3>
            <p className="mt-1 text-sm text-slate-600">
              The Commission hasn&rsquo;t issued an order in this proceeding yet.
              When it does, you&rsquo;ll find a plain-language breakdown here — what
              was decided, what you&rsquo;ll need to do, and by when. If you follow
              this docket, your digest will flag the order the week it drops.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div>
            <h3 className="font-semibold text-red-800">
              Couldn't analyze the order
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {state.detail ||
                'Something went wrong reading the order. Try again shortly.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const a = state.data.analysis || {};
  const effective = formatDate(a.effective_date);
  const adopted = formatDate(a.adoption_date);
  const released = formatDate(a.release_date);
  const recon = formatDate(a.reconsideration_deadline);
  const obligations = a.compliance_obligations || [];
  const ruleChanges = a.rule_changes || [];
  const takeaways = a.key_takeaways || [];
  const votes = a.votes || [];

  return (
    <div className="space-y-6">
      {/* Header band: what this is + the dates that matter */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-slate-800">
          <Gavel className="h-5 w-5 text-cg-ink" />
          <h2 className="text-lg font-semibold">What the Commission decided</h2>
        </div>
        {a.executive_summary && (
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            {a.executive_summary}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {effective && (
            <div className="flex items-center gap-1.5">
              <CalendarClock className="h-4 w-4 text-emerald-600" />
              <span className="text-slate-500">Effective</span>
              <span className="font-medium text-slate-800">{effective}</span>
            </div>
          )}
          {adopted && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Adopted</span>
              <span className="font-medium text-slate-800">{adopted}</span>
            </div>
          )}
          {released && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Released</span>
              <span className="font-medium text-slate-800">{released}</span>
            </div>
          )}
          {a.vote_breakdown && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Vote</span>
              <span className="font-medium text-slate-800">
                {a.vote_breakdown}
              </span>
            </div>
          )}
          {recon && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Reconsideration deadline</span>
              <span className="font-medium text-slate-800">{recon}</span>
            </div>
          )}
        </div>
      </div>

      {/* Key takeaways */}
      {takeaways.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-slate-800">
            <ScrollText className="h-5 w-5 text-cg-ink" />
            <h3 className="font-semibold">Key takeaways</h3>
          </div>
          <ul className="mt-3 space-y-2">
            {takeaways.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-700">
                <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cg-ink" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What you need to do — the compliance checklist */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-slate-800">
          <CheckSquare className="h-5 w-5 text-cg-ink" />
          <h3 className="font-semibold">What you need to do</h3>
          {obligations.length > 0 && (
            <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {obligations.length}
            </span>
          )}
        </div>
        {obligations.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            No specific compliance obligations were identified in this order.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {obligations.map((o, i) => (
              <li
                key={i}
                className="rounded-md border border-slate-200 bg-slate-50/60 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-slate-800">
                    {o.action_required}
                  </p>
                  <PriorityBadge priority={o.priority} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                  {(o.deadline_text || o.deadline) && (
                    <span className="flex items-center gap-1">
                      <CalendarClock className="h-3.5 w-3.5 text-amber-600" />
                      Deadline:{' '}
                      <span className="font-medium text-slate-700">
                        {o.deadline_text || formatDate(o.deadline)}
                      </span>
                    </span>
                  )}
                  {o.affected_entities && o.affected_entities.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-slate-500" />
                      {o.affected_entities.join(', ')}
                    </span>
                  )}
                  {o.paragraph_citation && (
                    <span className="text-slate-400">{o.paragraph_citation}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* What changed */}
      {ruleChanges.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-slate-800">
            <ArrowRightLeft className="h-5 w-5 text-cg-ink" />
            <h3 className="font-semibold">What changed</h3>
          </div>
          <ul className="mt-4 space-y-3">
            {ruleChanges.map((c, i) => (
              <li
                key={i}
                className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-sm"
              >
                {c.rule_section && (
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {c.rule_section}
                  </div>
                )}
                <p className="text-slate-800">{c.new_rule}</p>
                {c.prior_rule && (
                  <p className="mt-1 text-xs text-slate-500">
                    Previously: {c.prior_rule}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
                  {c.effective_date && (
                    <span>Effective {formatDate(c.effective_date)}</span>
                  )}
                  {c.paragraph_citation && <span>{c.paragraph_citation}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Votes / dissents — the signal */}
      {votes.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-slate-800">
            <Users className="h-5 w-5 text-cg-ink" />
            <h3 className="font-semibold">How the Commissioners voted</h3>
          </div>
          <ul className="mt-3 space-y-2">
            {votes.map((v, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium text-slate-800">
                  {v.commissioner_name}
                </span>
                <span className="text-slate-500"> — {v.vote}</span>
                {v.statement_summary && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    {v.statement_summary}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-slate-400">
        Auto-generated from the Commission's order. Verify specific obligations
        and deadlines against the source order before acting on them.
      </p>
    </div>
  );
}

export default OrderSection;
